import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { STATUS_CODES } from '@/constants'
import {
  FeatureFlagsSchema,
  defaultFeatureFlags,
  type FeatureFlags,
  FeatureFlag,
  type FeatureFlagType,
  flattenFeatureFlags,
  groupByCategory
} from '@/types/featureFlags'
import { isClient, safeLocalStorage } from '@/composables/useSSR'

// Re-export FeatureFlagType for convenience
export type { FeatureFlagType } from '@/types/featureFlags'

const CACHE_KEY = 'itsh_feature_flags'
const CACHE_EXPIRY_MS = 5 * 60 * 1000 // 5 minutes

interface CachedFlags {
  flags: FeatureFlags
  timestamp: number
}

export const useFeatureFlagsStore = defineStore('featureFlags', () => {
  const flags = ref<FeatureFlags>(defaultFeatureFlags)
  const loaded = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Admin-specific state
  const adminFlags = ref<FeatureFlagType[]>([])
  const adminLoading = ref(false)
  const adminError = ref<string | null>(null)

  // Storage event handler for cross-tab sync
  let storageHandler: ((event: StorageEvent) => void) | null = null

  /**
   * Check if a flag is enabled (dynamic lookup)
   * Returns false if flag doesn't exist in database
   */
  function isEnabled(category: string, name: string): boolean {
    return flags.value[category]?.[name] ?? false
  }

  /**
   * Load flags from localStorage cache (synchronous, for fast startup)
   */
  function loadFromCache(): boolean {
    const storage = safeLocalStorage()
    if (!storage) return false

    try {
      const cached = storage.getItem(CACHE_KEY)
      if (!cached) return false

      const parsed: CachedFlags = JSON.parse(cached)
      const now = Date.now()

      // Check if cache is still valid
      if (now - parsed.timestamp > CACHE_EXPIRY_MS) {
        storage.removeItem(CACHE_KEY)
        return false
      }

      // Validate with Zod schema
      const result = FeatureFlagsSchema.safeParse(parsed.flags)
      if (result.success) {
        flags.value = result.data
        loaded.value = true
        return true
      }

      // Invalid cache structure, remove it
      storage.removeItem(CACHE_KEY)
      return false
    } catch {
      // Parse error or other issue
      storage.removeItem(CACHE_KEY)
      return false
    }
  }

  /**
   * Save flags to localStorage cache
   */
  function saveToCache(flagsData: FeatureFlags): void {
    const storage = safeLocalStorage()
    if (!storage) return

    try {
      const cacheData: CachedFlags = {
        flags: flagsData,
        timestamp: Date.now()
      }
      storage.setItem(CACHE_KEY, JSON.stringify(cacheData))
    } catch {
      // localStorage might be full or disabled, ignore
    }
  }

  /**
   * Fetch flags from backend API
   */
  async function fetchFlags(): Promise<void> {
    if (loading.value) return

    loading.value = true
    error.value = null

    try {
      const response = await useAxios<never, unknown>(
        'GET',
        '/config/features',
        false // Public endpoint, no auth required
      )

      // Validate and parse response with defaults applied
      const result = FeatureFlagsSchema.safeParse(response.data)
      if (!result.success) {
        throw new Error('Invalid feature flags response')
      }

      flags.value = result.data
      loaded.value = true
      saveToCache(result.data)
    } catch (err) {
      // API failed - keep using cached/default values
      error.value = err instanceof Error ? err.message : 'Failed to fetch feature flags'

      // If we haven't loaded from cache yet, use defaults
      if (!loaded.value) {
        flags.value = defaultFeatureFlags
        loaded.value = true
      }
    } finally {
      loading.value = false
    }
  }

  /**
   * Start listening for cross-tab storage changes
   */
  function startStorageSync(): void {
    if (storageHandler || !isClient) return // Already listening or SSR

    storageHandler = (event: StorageEvent) => {
      if (event.key === CACHE_KEY && event.newValue) {
        try {
          const parsed: CachedFlags = JSON.parse(event.newValue)
          const result = FeatureFlagsSchema.safeParse(parsed.flags)
          if (result.success) {
            flags.value = result.data
          }
        } catch {
          // Invalid data from other tab, ignore
        }
      }
    }
    window.addEventListener('storage', storageHandler)
  }

  /**
   * Stop listening for storage changes
   */
  function stopStorageSync(): void {
    if (!isClient) return

    if (storageHandler) {
      window.removeEventListener('storage', storageHandler)
      storageHandler = null
    }
  }

  /**
   * Initialize the store - load from cache and defer API fetch for better performance
   */
  function initialize(): void {
    loadFromCache()
    if (isClient) {
      startStorageSync()
      // Defer network request until browser is idle to not block hydration
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => fetchFlags(), { timeout: 2000 })
      } else {
        // Fallback for Safari
        setTimeout(() => fetchFlags(), 100)
      }
    }
  }

  /**
   * Force refresh flags from API
   */
  async function refresh(): Promise<void> {
    await fetchFlags()
  }

  // ========================================
  // Admin CRUD Operations
  // ========================================

  /**
   * Computed getters for admin view
   */
  const adminFlagsGrouped = computed(() => groupByCategory(adminFlags.value))
  const adminCategories = computed(() =>
    [...new Set(adminFlags.value.map((f) => f.category))].sort()
  )
  const adminFlagCount = computed(() => adminFlags.value.length)
  const adminEnabledCount = computed(
    () => adminFlags.value.filter((f) => f.enabled).length
  )
  const adminDisabledCount = computed(
    () => adminFlags.value.filter((f) => !f.enabled).length
  )

  /**
   * Fetch all flags for admin management (bypasses cache)
   */
  async function fetchAdminFlags(): Promise<void> {
    if (adminLoading.value) return

    adminLoading.value = true
    adminError.value = null

    try {
      const response = await useAxios<never, Record<string, Record<string, boolean>>>(
        'GET',
        '/config/features',
        true // Use auth for admin view
      )

      if (response.status === STATUS_CODES.OK && response.data) {
        adminFlags.value = flattenFeatureFlags(response.data)
      }
    } catch (err) {
      adminError.value =
        err instanceof Error ? err.message : 'Fehler beim Laden der Feature Flags'
      handleAxiosError(err, { logPrefix: 'FeatureFlagsStore:fetchAdminFlags' })
    } finally {
      adminLoading.value = false
    }
  }

  /**
   * Create or update a feature flag
   */
  async function createOrUpdateFlag(flag: FeatureFlagType): Promise<boolean> {
    // Validate with Zod
    const validation = FeatureFlag.safeParse(flag)
    if (!validation.success) {
      adminError.value = 'Ungültige Flag-Daten'
      return false
    }

    try {
      const response = await useAxios<FeatureFlagType, FeatureFlagType>(
        'POST',
        '/config/features',
        true,
        validation.data
      )

      if (response.status === STATUS_CODES.OK || response.status === STATUS_CODES.CREATED) {
        // Refresh the list to get updated data
        await fetchAdminFlags()
        // Also refresh the main flags cache
        await fetchFlags()
        return true
      }

      return false
    } catch (err) {
      adminError.value =
        err instanceof Error ? err.message : 'Fehler beim Speichern des Flags'
      handleAxiosError(err, { logPrefix: 'FeatureFlagsStore:createOrUpdateFlag' })
      return false
    }
  }

  /**
   * Delete a feature flag
   */
  async function deleteFlag(category: string, name: string): Promise<boolean> {
    try {
      const response = await useAxios(
        'DELETE',
        `/config/features/${encodeURIComponent(category)}/${encodeURIComponent(name)}`,
        true
      )

      if (response.status === STATUS_CODES.NO_CONTENT || response.status === STATUS_CODES.OK) {
        // Remove from local state immediately
        adminFlags.value = adminFlags.value.filter(
          (f) => !(f.category === category && f.name === name)
        )
        // Refresh the main flags cache
        await fetchFlags()
        return true
      }

      return false
    } catch (err) {
      adminError.value =
        err instanceof Error ? err.message : 'Fehler beim Löschen des Flags'
      handleAxiosError(err, { logPrefix: 'FeatureFlagsStore:deleteFlag' })
      return false
    }
  }

  /**
   * Toggle a flag's enabled state (quick action)
   */
  async function toggleFlag(category: string, name: string): Promise<boolean> {
    const flag = adminFlags.value.find(
      (f) => f.category === category && f.name === name
    )
    if (!flag) return false

    return createOrUpdateFlag({
      category,
      name,
      enabled: !flag.enabled
    })
  }

  return {
    // State
    flags,
    loaded,
    loading,
    error,

    // Dynamic flag checker
    isEnabled,

    // Actions
    loadFromCache,
    fetchFlags,
    initialize,
    refresh,
    startStorageSync,
    stopStorageSync,

    // Admin state
    adminFlags,
    adminLoading,
    adminError,
    adminFlagsGrouped,
    adminCategories,
    adminFlagCount,
    adminEnabledCount,
    adminDisabledCount,

    // Admin actions
    fetchAdminFlags,
    createOrUpdateFlag,
    deleteFlag,
    toggleFlag
  }
})
